import React, { useState, useEffect, useCallback } from "react";
import { fetchExpertsList } from "../utils/api/expertApi.js";
import api from "../utils/api.js";
import { getImageUrl } from "../utils/imageUtils.js";
import { useLanguage } from "../context/LanguageContext.jsx";

const CONTENT = {
  ne: {
    pageTitleHighlight: "हाम्रा",
    pageTitleSuffix: "विज्ञहरू",
    headerDesc: "विभिन्न क्षेत्रका अनुभवी विज्ञहरू जसले भोलिको इटहरी निर्माणमा योगदान गरिरहनुभएको छ।",
    searchPlaceholder: "नाम वा शिक्षाद्वारा खोज्नुहोस्...",
    allSectors: "सबै क्षेत्र",
    loading: "विज्ञहरू लोड हुँदैछ…",
    empty: "कुनै विज्ञ भेटिएन।",
    education: "शिक्षा",
    yearOfExcellence: "उत्कृष्टता वर्ष",
    address: "ठेगाना",
    errLoad: "विज्ञहरू लोड गर्न असफल भयो।",
  },
  en: {
    pageTitleHighlight: "Our",
    pageTitleSuffix: "Experts",
    headerDesc: "Experienced experts across sectors contributing to building Bholiko Itahari.",
    searchPlaceholder: "Search by name or education...",
    allSectors: "All Sectors",
    loading: "Loading experts…",
    empty: "No experts found.",
    education: "Education",
    yearOfExcellence: "Year of Excellence",
    address: "Address",
    errLoad: "Failed to load experts.",
  },
};

function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const ExpertsListingPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;

  const [experts, setExperts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeSector, setActiveSector] = useState("All");

  const debouncedSearch = useDebouncedValue(search, 400);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/sectors");
        const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.sectors ?? []);
        const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setSectors(sorted);
      } catch (e) {
        console.error("Error fetching sectors:", e);
      }
    })();
  }, []);

  const loadExperts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchExpertsList({
        sector: activeSector,
        search: debouncedSearch || undefined,
      });
      const data = Array.isArray(res) ? res : (res?.data ?? []);
      setExperts(data);
    } catch (e) {
      setError(e.response?.data?.message || t.errLoad);
    } finally {
      setLoading(false);
    }
  }, [activeSector, debouncedSearch, t.errLoad]);

  useEffect(() => {
    loadExperts();
  }, [loadExperts]);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-12">

        <div className="mb-6">
          <h1
            className="text-gray-900 text-3xl lg:text-4xl font-bold leading-tight mb-2"
            style={{ fontFamily: "'Playfair Display',serif" }}
          >
            {t.pageTitleHighlight} <span className="text-orange-500">{t.pageTitleSuffix}</span>
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed max-w-lg">
            {t.headerDesc}
          </p>
        </div>
{/* Filters */}
<div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
  <div className="flex flex-col sm:flex-row gap-3">

    {/* Search */}
    <div className="relative flex-1">
      <div className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-green-50 flex items-center justify-center pointer-events-none">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgb(21,75,43)" strokeWidth="2.2" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" />
        </svg>
      </div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t.searchPlaceholder}
        className="w-full pl-11 pr-9 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 outline-none transition-all focus:bg-white focus:border-green-700 focus:ring-4 focus:ring-green-700/10"
      />
      {search && (
        <button
          onClick={() => setSearch("")}
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors text-sm"
        >
          
        </button>
      )}
    </div>

    {/* Sector dropdown */}
    <div className="relative sm:w-64">
      <select
        value={activeSector}
        onChange={(e) => setActiveSector(e.target.value)}
        className="w-full appearance-none px-4 py-2.5 pr-9 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 outline-none transition-all focus:bg-white focus:border-green-700 focus:ring-4 focus:ring-green-700/10 cursor-pointer"
      >
        <option value="All">{t.allSectors}</option>
        {sectors.map((s) => (
          <option key={s._id} value={s.name}>{s.name}</option>
        ))}
      </select>
      <span className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">▾</span>
    </div>

  </div>
</div>

        {/* Grid */}
        <div className="mt-8">
          {error && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {loading && (
            <div className="text-center py-20 text-gray-400 text-sm">{t.loading}</div>
          )}

          {!loading && experts.length === 0 && (
            <div className="text-center py-20 text-gray-400 text-sm">{t.empty}</div>
          )}

          {!loading && experts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {experts.map((ex) => (
                <div
                  key={ex._id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-6 flex flex-col items-center text-center"
                >
                  {ex.photoUrl ? (
                    <img
                      src={getImageUrl(ex.photoUrl)}
                      alt={ex.name}
                      className="w-24 h-24 rounded-full object-cover mb-4"
                      onError={(e) => (e.target.style.opacity = "0.3")}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-3xl text-gray-300 mb-4">
                      👤
                    </div>
                  )}

                  <h3 className="text-base font-bold text-gray-900 mb-1">{ex.name}</h3>

                  {ex.sector && (
                    <span
                      className="inline-block text-[0.7rem] font-semibold px-3 py-1 rounded-full mb-3"
                      style={{ background: "rgba(21,75,43,0.08)", color: "rgb(21,75,43)" }}
                    >
                      {ex.sector}
                    </span>
                  )}

                  <div className="w-full text-left text-xs text-gray-500 space-y-1.5 mt-1">
                    {ex.education && (
                      <p><span className="font-semibold text-gray-600">{t.education}:</span> {ex.education}</p>
                    )}
                    {ex.yearOfExcellence && (
                      <p><span className="font-semibold text-gray-600">{t.yearOfExcellence}:</span> {ex.yearOfExcellence}</p>
                    )}
                    {ex.address && (
                      <p><span className="font-semibold text-gray-600">{t.address}:</span> {ex.address}</p>
                    )}
                    {ex.additionalInfo && (
                      <p className="text-gray-400 mt-2 leading-relaxed">{ex.additionalInfo}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertsListingPage;